import {
  newsHtmlSanitizerPolicy,
  type ContentType,
  type PublicBanner,
  type PublicFaq,
  type PublicHeroSlide,
  type PublicNoticia,
  type PublicOferta,
  type PublicPartner,
} from '@apuestapr/contracts';
import { buttonVariants, cn } from '@apuestapr/ui';
import DOMPurify, { type Config } from 'dompurify';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import type { ContentPreviewByType } from './catalog.ts';
import { formatPuertoRicoInstant, puertoRicoZoneLabel } from './ContentListPage.tsx';
import { VisibilityBadge } from './VisibilityBadge.tsx';

/**
 * Authored news HTML has a deliberately narrow formatting vocabulary. URL-bearing attributes are
 * limited to safe HTTPS or single-slash site-relative links; everything else is removed.
 *
 * The vocabulary itself moved to `newsHtmlSanitizerPolicy` in `@apuestapr/contracts` at Task 10.1,
 * where the public site reads the same object. This preview and the visitor-facing news route are the
 * only two places authored HTML is rendered, and they now share one allowlist rather than two copies
 * that agreed on the day they were written. The projection below is mechanical: the library wants
 * mutable arrays, the shared policy is readonly.
 */
export const newsHtmlSanitizerConfig = {
  ALLOWED_TAGS: [...newsHtmlSanitizerPolicy.ALLOWED_TAGS],
  ALLOWED_ATTR: [...newsHtmlSanitizerPolicy.ALLOWED_ATTR],
  ALLOWED_URI_REGEXP: newsHtmlSanitizerPolicy.ALLOWED_URI_REGEXP,
  ALLOW_UNKNOWN_PROTOCOLS: newsHtmlSanitizerPolicy.ALLOW_UNKNOWN_PROTOCOLS,
  FORBID_ATTR: [...newsHtmlSanitizerPolicy.FORBID_ATTR],
  FORBID_TAGS: [...newsHtmlSanitizerPolicy.FORBID_TAGS],
  RETURN_TRUSTED_TYPE: newsHtmlSanitizerPolicy.RETURN_TRUSTED_TYPE,
} satisfies Config;

/** The only function allowed to produce HTML for the preview injection boundary. */
export const sanitizeNewsHtml = (authoredHtml: string): string =>
  DOMPurify.sanitize(authoredHtml, newsHtmlSanitizerConfig);

const NullableText = ({
  value,
  absent,
}: {
  readonly value: string | null;
  readonly absent: string;
}) =>
  value === null ? <span className="text-muted-foreground">{absent}</span> : <span>{value}</span>;

const PreviewImage = ({ src, alt }: { readonly src: string | null; readonly alt: string }) =>
  src === null ? (
    <p className="text-muted-foreground">Sin imagen.</p>
  ) : (
    <img alt={alt} className="max-h-96 max-w-full rounded-xl object-contain" src={src} />
  );

const PreviewLink = ({
  href,
  label,
}: {
  readonly href: string | null;
  readonly label: string | null;
}) =>
  href === null || label === null ? (
    <p className="text-muted-foreground">Sin llamado a la acción.</p>
  ) : (
    <a className={buttonVariants({ variant: 'primary' })} href={href} rel="noreferrer">
      {label}
    </a>
  );

const OfertaPreview = ({ content }: { readonly content: PublicOferta }): ReactElement => (
  <article className="grid gap-4">
    <h3 className="m-0">{content.titulo}</h3>
    <p className="m-0">{content.descripcion}</p>
    <PreviewImage alt={content.titulo} src={content.imagenUrl} />
    <p className="m-0">Categoría: {content.categoria}</p>
    <p className="m-0">
      Fin de disponibilidad:{' '}
      {content.unpublishAt === null ? (
        'Sin fecha'
      ) : (
        <time dateTime={content.unpublishAt}>{formatPuertoRicoInstant(content.unpublishAt)}</time>
      )}
    </p>
  </article>
);

const NoticiaPreview = ({ content }: { readonly content: PublicNoticia }): ReactElement => {
  const sanitized = sanitizeNewsHtml(content.contenido);
  return (
    <article className="grid gap-4">
      <h3 className="m-0">{content.titulo}</h3>
      <p className="m-0">Ruta de la noticia: /noticias/{content.slug}</p>
      <PreviewImage alt={content.titulo} src={content.imagenUrl} />
      <p className="m-0">Categoría: {content.categoria}</p>
      <p className="m-0">
        Publicada:{' '}
        <time dateTime={content.createdAt}>{formatPuertoRicoInstant(content.createdAt)}</time>
      </p>
      <div
        className="grid gap-3"
        data-testid="news-preview-body"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    </article>
  );
};

const FaqPreview = ({ content }: { readonly content: PublicFaq }): ReactElement => (
  <article className="grid gap-3">
    <h3 className="m-0">{content.question}</h3>
    <p className="m-0">{content.answer}</p>
    <p className="m-0">Categoría: {content.category}</p>
  </article>
);

const BannerPreview = ({ content }: { readonly content: PublicBanner }): ReactElement => (
  <article className="grid gap-3">
    <p className="m-0 text-sm text-muted-foreground">Identidad de cierre: {content.slug}</p>
    <h3 className="m-0">{content.titulo}</h3>
    <p className="m-0">{content.mensaje}</p>
    <p className="m-0">Ubicación: {content.placement}</p>
    <PreviewLink href={content.ctaUrl} label={content.ctaLabel} />
  </article>
);

const HeroPreview = ({ content }: { readonly content: PublicHeroSlide }): ReactElement => (
  <article className="grid gap-4">
    <PreviewImage alt={content.imagenAlt ?? ''} src={content.imagenUrl} />
    <h3 className="m-0">{content.titulo}</h3>
    <p className="m-0">
      <NullableText absent="Sin descripción." value={content.descripcion} />
    </p>
    <p className="m-0">
      Texto alternativo: <NullableText absent="Sin texto alternativo." value={content.imagenAlt} />
    </p>
    <PreviewLink href={content.ctaUrl} label={content.ctaLabel} />
  </article>
);

const PartnerPreview = ({ content }: { readonly content: PublicPartner }): ReactElement => (
  <article className="grid gap-4">
    <h3 className="m-0">{content.nombre}</h3>
    <PreviewImage alt={`Logo de ${content.nombre}`} src={content.logoUrl} />
    {content.sitioUrl === null ? (
      <p className="text-muted-foreground">Sin sitio web.</p>
    ) : (
      <a href={content.sitioUrl} rel="noreferrer">
        Visitar {content.nombre}
      </a>
    )}
  </article>
);

const VisitorPreview = ({
  type,
  content,
}: {
  readonly type: ContentType;
  readonly content: ContentPreviewByType[ContentType]['content'];
}): ReactElement => {
  switch (type) {
    case 'ofertas':
      return <OfertaPreview content={content as PublicOferta} />;
    case 'noticias':
      return <NoticiaPreview content={content as PublicNoticia} />;
    case 'faqs':
      return <FaqPreview content={content as PublicFaq} />;
    case 'banners':
      return <BannerPreview content={content as PublicBanner} />;
    case 'hero-slides':
      return <HeroPreview content={content as PublicHeroSlide} />;
    case 'partners':
      return <PartnerPreview content={content as PublicPartner} />;
  }
};

export interface ContentPreviewPageProps {
  readonly type: ContentType;
  readonly preview: ContentPreviewByType[ContentType];
}

/** Renders the exact public projection returned by the authenticated admin preview endpoint. */
export const ContentPreviewPage = ({ type, preview }: ContentPreviewPageProps): ReactElement => (
  <section aria-labelledby="content-preview-heading" className="grid gap-6">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="m-0" id="content-preview-heading">
          Vista previa
        </h2>
        <p className="mb-0 text-muted-foreground">
          Esta vista requiere una sesión del panel y no crea un enlace público compartible.
        </p>
      </div>
      <Link className={cn(buttonVariants({ variant: 'outline' }))} to="../edit">
        Volver a editar
      </Link>
    </div>
    <aside
      aria-label="Visibilidad efectiva"
      className="grid gap-2 rounded-xl border border-border bg-card p-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <strong>Visibilidad efectiva:</strong>
        <VisibilityBadge visibility={preview.effectiveVisibility} />
      </div>
      {preview.effectiveVisibility === 'scheduled' && preview.publishAt !== null ? (
        <p className="m-0">
          Se hará visible el{' '}
          <time dateTime={preview.publishAt}>{formatPuertoRicoInstant(preview.publishAt)}</time>.{' '}
          <span className="text-muted-foreground">{puertoRicoZoneLabel}</span>
        </p>
      ) : null}
    </aside>
    <div
      aria-label="Contenido tal como lo verá el visitante"
      className="rounded-xl border border-border bg-card p-6"
    >
      <VisitorPreview content={preview.content} type={type} />
    </div>
  </section>
);
